import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useErrorStore } from '@/stores/errorStore';
import { useAuthStore } from '@/stores/authStore';
import {
  getErrorCode,
  getErrorSeverity,
  isNetworkError,
  isRetryableError,
  ErrorSeverity,
} from '@/types/errors';
import { isPublicInterviewRoute } from '@/utils/routeHelpers';

interface ApiErrorResponse {
  detail?: string;
  message?: string;
  error?: string;
  error_code?: string;
}

const GlobalErrorHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const showError = useErrorStore((state) => state.showError);
  const showWarning = useErrorStore((state) => state.showWarning);
  const showInfo = useErrorStore((state) => state.showInfo);
  const addError = useErrorStore((state) => state.addError);
  const clearAuth = useAuthStore((state) => state.clearAuth);

  // Track error frequency to prevent spam
  const errorFrequencyRef = useRef<Map<string, number>>(new Map());
  const lastErrorTimeRef = useRef<Map<string, number>>(new Map());

  // Constants for memory management
  const MAX_ERROR_ENTRIES = 100;
  const ERROR_ENTRY_MAX_AGE_MS = 60000; // 1 minute

  const shouldShowError = useCallback((errorKey: string): boolean => {
    const now = Date.now();
    const lastTime = lastErrorTimeRef.current.get(errorKey) || 0;
    const frequency = errorFrequencyRef.current.get(errorKey) || 0;

    // Reset frequency counter if more than 10 seconds passed
    if (now - lastTime > 10000) {
      errorFrequencyRef.current.set(errorKey, 0);

      // Cleanup old entries to prevent memory leaks
      // Remove entries older than 1 minute
      const keysToDelete: string[] = [];
      lastErrorTimeRef.current.forEach((timestamp, key) => {
        if (now - timestamp > ERROR_ENTRY_MAX_AGE_MS) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach((key) => {
        lastErrorTimeRef.current.delete(key);
        errorFrequencyRef.current.delete(key);
      });

      // If still over limit, remove oldest entries (LRU-style)
      if (lastErrorTimeRef.current.size > MAX_ERROR_ENTRIES) {
        const entries = Array.from(lastErrorTimeRef.current.entries());
        entries.sort((a, b) => a[1] - b[1]); // Sort by timestamp ascending (oldest first)
        const entriesToRemove = entries.slice(0, entries.length - MAX_ERROR_ENTRIES);
        entriesToRemove.forEach(([key]) => {
          lastErrorTimeRef.current.delete(key);
          errorFrequencyRef.current.delete(key);
        });
      }
    }

    // Don't show if same error occurred more than 3 times in 10 seconds
    if (frequency >= 3 && now - lastTime < 10000) {
      return false;
    }

    errorFrequencyRef.current.set(errorKey, frequency + 1);
    lastErrorTimeRef.current.set(errorKey, now);
    return true;
  }, []);

  const handleChunkLoadFailure = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const reloadKey = 'mm-chunk-reload-v1';
    try {
      if (sessionStorage.getItem(reloadKey)) {
        return;
      }
      sessionStorage.setItem(reloadKey, '1');
    } catch (error) {
      // Continue with reload even if sessionStorage is unavailable.
    }

    const purgeCaches = async () => {
      const hasServiceWorker = 'serviceWorker' in navigator;
      const hasCaches = 'caches' in window;

      const registrations = hasServiceWorker ? await navigator.serviceWorker.getRegistrations() : [];
      const cacheKeys = hasCaches ? await caches.keys() : [];

      await Promise.all(registrations.map((registration) => registration.unregister()));
      await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
    };

    purgeCaches()
      .catch((error) => {
        console.warn('Failed to purge caches after chunk load error:', error);
      })
      .finally(() => {
        window.location.reload();
      });
  }, []);

  const handleError = useCallback((error: Error | AxiosError) => {
    // Check if it's an Axios error
    if (isAxiosError(error)) {
      handleAxiosError(error);
    } else {
      // Generic error handling with severity assessment
      const severity = getErrorSeverity(error);
      const errorKey = `generic-${error.message}`;

      if (!shouldShowError(errorKey)) {
        console.warn('Suppressing repeated error:', error.message);
        return;
      }

      // Only show toast for critical errors
      if (severity === ErrorSeverity.CRITICAL || severity === ErrorSeverity.HIGH) {
        showError(
          error.message || 'An unexpected error occurred',
          'Error'
        );
      }

      // Log to error store
      addError({
        message: error.message,
        details: error,
      });
    }
  }, [showError, addError, shouldShowError]);

  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);

      // Prevent handling chunk loading errors here - let the error boundary handle them
      const reason = event.reason;
      if (
        reason &&
        typeof reason === 'object' &&
        'message' in reason &&
        typeof reason.message === 'string' &&
        (reason.message.includes('Loading chunk') ||
         reason.message.includes('dynamically imported module'))
      ) {
        console.warn('Chunk loading error detected, forcing refresh.');
        handleChunkLoadFailure();
        event.preventDefault();
        return;
      }

      if (event.reason instanceof Error) {
        handleError(event.reason);
      } else {
        showError(
          'An unexpected error occurred. Please try again.',
          'Unexpected Error'
        );
      }

      // Prevent default browser behavior
      event.preventDefault();
    };

    // Handle global errors
    const handleGlobalError = (event: ErrorEvent) => {
      console.error('Global error:', event.error);

      // Skip resource loading errors (images, scripts, etc.)
      if (event.target && event.target !== window) {
        return;
      }

      if (event.error instanceof Error) {
        handleError(event.error);
      }

      // Prevent default browser behavior
      event.preventDefault();
    };

    const handleVitePreloadError = () => {
      handleChunkLoadFailure();
    };

    // Handle online/offline events
    const handleOnline = () => {
      showInfo('Connection restored', 'You are back online');
    };

    const handleOffline = () => {
      showWarning('Connection lost', 'You are currently offline');
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('vite:preloadError', handleVitePreloadError as EventListener);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('vite:preloadError', handleVitePreloadError as EventListener);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleError, handleChunkLoadFailure, showError, showWarning, showInfo]);

  const isAxiosError = (error: any): error is AxiosError => {
    return error.isAxiosError === true;
  };

  const handleAxiosError = useCallback((error: AxiosError<any>) => {
    const statusCode = error.response?.status;
    const errorData = error.response?.data;
    const errorCodeFromApi = getErrorCode(error);

    // Extract error message
    const errorMessage =
      errorData?.detail ||
      errorData?.message ||
      errorData?.error ||
      'An error occurred';

    // Create error key for frequency tracking
    const errorKey = `axios-${statusCode}-${errorCodeFromApi || errorMessage}`;

    // Check if we should suppress this error
    if (!shouldShowError(errorKey)) {
      console.warn('Suppressing repeated API error:', errorMessage);
      return;
    }

    // Check if error is retryable and suggest action
    const canRetry = isRetryableError(error);
    const isNetwork = isNetworkError(error);

    // Handle different HTTP status codes
    switch (statusCode) {
      case 400:
        // Bad Request
        showError(
          errorMessage,
          'Invalid Request'
        );
        addError({
          message: errorMessage,
          code: 'BAD_REQUEST',
          statusCode,
          details: errorData,
        });
        break;

      case 401:
        if (isPublicInterviewRoute()) {
          break;
        }
        // Unauthorized - redirect to login only if not already on /login
        showWarning(
          'Your session has expired. Please log in again.',
          'Session Expired'
        );
        clearAuth();
        if (location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
        addError({
          message: 'Unauthorized access',
          code: 'UNAUTHORIZED',
          statusCode,
        });
        break;

      case 403:
        // Forbidden - show access denied page
        showError(
          'You do not have permission to access this resource.',
          'Access Denied'
        );
        navigate('/access-denied', { replace: true });
        addError({
          message: 'Access denied',
          code: 'FORBIDDEN',
          statusCode,
        });
        break;

      case 404:
        // Not Found
        showError(
          errorMessage || 'The requested resource was not found.',
          'Not Found'
        );
        addError({
          message: errorMessage,
          code: 'NOT_FOUND',
          statusCode,
        });
        break;

      case 409:
        // Conflict
        showError(
          errorMessage || 'The request could not be completed due to a conflict.',
          'Conflict'
        );
        addError({
          message: errorMessage,
          code: 'CONFLICT',
          statusCode,
        });
        break;

      case 422:
        // Validation Error
        showError(
          errorMessage || 'The submitted data is invalid.',
          'Validation Error'
        );
        addError({
          message: errorMessage,
          code: 'VALIDATION_ERROR',
          statusCode,
          details: errorData,
        });
        break;

      case 429:
        // Too Many Requests
        showWarning(
          'Too many requests. Please slow down and try again later.',
          'Rate Limit Exceeded'
        );
        addError({
          message: 'Rate limit exceeded',
          code: 'RATE_LIMIT',
          statusCode,
        });
        break;

      case 500:
        // Internal Server Error
        showError(
          'A server error occurred. Our team has been notified.',
          'Server Error'
        );
        addError({
          message: errorMessage,
          code: 'INTERNAL_SERVER_ERROR',
          statusCode,
          details: errorData,
        });
        break;

      case 502:
      case 503:
      case 504:
        // Service Unavailable / Gateway errors
        showError(
          'The service is temporarily unavailable. Please try again later.',
          'Service Unavailable'
        );
        addError({
          message: 'Service unavailable',
          code: 'SERVICE_UNAVAILABLE',
          statusCode,
        });
        break;

      default:
        // Network error or unknown error
        if (isNetwork) {
          showError(
            'Unable to connect to the server. Please check your internet connection.',
            'Network Error'
          );
          addError({
            message: 'Network error',
            code: 'NETWORK_ERROR',
            details: error,
          });
        } else if (canRetry) {
          showWarning(
            `${errorMessage} Please try again.`,
            'Temporary Error'
          );
          addError({
            message: errorMessage,
            statusCode,
            code: errorCodeFromApi,
            details: errorData,
          });
        } else {
          showError(
            errorMessage || 'An unexpected error occurred.',
            'Error'
          );
          addError({
            message: errorMessage,
            statusCode,
            code: errorCodeFromApi,
            details: errorData,
          });
        }
    }
  }, [navigate, location.pathname, showError, showWarning, addError, clearAuth, shouldShowError]);

  // This component doesn't render anything
  return null;
};

export default GlobalErrorHandler;
